/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import moment from 'moment';
import backendInfo from 'services/backend-info.service';
import {PortalError} from 'utility/error-handler';
import {getBrowserLimits} from 'components/shared/utils';

/* eslint-disable @typescript-eslint/no-explicit-any */

const defaultRequestTimeout = moment.duration(15, 'second').asMilliseconds();

interface IReadableStreamWithAsyncIterator<R> extends ReadableStream {
  [Symbol.asyncIterator](): AsyncIterableIterator<R>;
}

type RetryConfig = {
  maxRetries?: number;
  delay?: number | number[];
};

interface IRequestMethod {
  url: string;
  headers: Headers;
  data?: Record<string, any>;
  options?: Record<string, any>;
  timeout?: number;
  retryConfig?: RetryConfig;
}

interface IFetchData extends IRequestMethod {
  method: string;
}

class FetchService {
  private browserLimits = getBrowserLimits();
  private async handleErrors(
    responseData: string,
    responseHeaders: Headers,
    responseStatus: number,
    method: string,
    url: string,
    data?: Record<string, any>
  ): Promise<void> {
    const contentType = responseHeaders.get('content-type');

    if (contentType?.includes('application/json')) {
      const errorData = await JSON.parse(responseData);
      const status = errorData.status;

      throw new PortalError(status || 'default', {
        statusCode: responseStatus,
        requestPayload: JSON.stringify({
          method,
          url,
          data
        })
      });
    }

    if (responseStatus === 404) {
      throw new PortalError('not-found', {
        message: 'This request URL is not found in our system',
        statusCode: responseStatus,
        requestPayload: JSON.stringify({
          method,
          url,
          data
        })
      });
    }

    throw new PortalError('no-status-error', {
      message: responseData,
      statusCode: responseStatus,
      requestPayload: JSON.stringify({
        method,
        url,
        data
      })
    });
  }

  private async fetchData({url, method, data, headers, options, timeout = defaultRequestTimeout}: IFetchData, abortController: AbortController): Promise<object | string | number | boolean | undefined | null> {
    const backendUri = await backendInfo.getBackendUri();

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const requestOptions: RequestInit = {
      ...options,
      method,
      signal: abortController.signal, // Set the internal controller's signal
      headers
    };

    if (data) {
      requestOptions.body = JSON.stringify(data);
    }

    const timeoutId = setTimeout(() => {
      abortController.abort('timeout');
    }, timeout);

    try {
      const response = await fetch(`${backendUri}${url}`, requestOptions);
      const readableStream = response.body as IReadableStreamWithAsyncIterator<Uint8Array>;
      const chunks = [];
      let receivedBytes = 0;

      for await (const value of readableStream) {
        chunks.push(value);
        receivedBytes += value.byteLength;

        if (receivedBytes > this.browserLimits?.bytes) {
          abortController.abort('exceeds-browser-limits');
        }
      }

      const chunksAll = new Uint8Array(receivedBytes);
      let position = 0;

      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }

      const responseText = new TextDecoder('utf-8').decode(chunksAll);

      if (!response.ok) {
        await this.handleErrors(responseText, response.headers, response.status, method, url, data);
      }

      const contentType = response.headers.get('content-type');

      return {
        status: response.status,
        headers: response.headers,
        data: contentType?.includes('application/json') ? JSON.parse(responseText) : responseText
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async fetchDataWithRetry({url, method, data, headers, options, retryConfig, timeout}: IFetchData): Promise<any> {
    const maxRetries = retryConfig?.maxRetries || 0;
    const delay = retryConfig?.delay || 0;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const abortController = new AbortController();

      try {
        return await this.fetchData({
          url,
          method,
          data,
          headers,
          options,
          timeout
        }, abortController);
      } catch (e) {
        // Handle errors based on retry configuration
        if (attempt === maxRetries) {
          if (abortController.signal.aborted) {
            if (abortController.signal.reason === 'timeout') {
              throw new PortalError('request-timeout', {
                message: `Request timeout. Your request lasted more than ${timeout || defaultRequestTimeout}ms`,
                requestPayload: JSON.stringify({
                  method,
                  url,
                  data
                })
              });
            }

            if (abortController.signal.reason === 'exceeds-browser-limits') {
              throw new PortalError('request-exceeds-browser-limits', {
                message: `Requested file exceeds the browser's download file size limit (${this.browserLimits?.value})`,
                requestPayload: JSON.stringify({
                  method,
                  url,
                  data
                })
              });
            }

            throw new PortalError('request-aborted', {
              message: 'Requested was aborted',
              requestPayload: JSON.stringify({
                method,
                url,
                data
              })
            });
          }

          // No more retries, throw the error
          throw e;
        }

        let retryDelay: number;

        if (Array.isArray(delay)) {
          // If delay is an array, use the corresponding delay
          retryDelay = delay[Math.min(attempt, delay.length - 1)];
        } else if (typeof delay === 'number') {
          // If delay is a number, use it for all retries
          retryDelay = delay;
        } else {
          // If delay is not a number or array, throw an error
          throw new PortalError('invalid-retry-delay', {
            message: 'Invalid retry delay configuration',
            requestPayload: JSON.stringify({
              method,
              url,
              data
            })
          });
        }

        // Apply the delay for retries
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }

  /**
   *  NOTE: An `Authorization` header need to be set on every call.
   *        `Authorization` header must contin a bearerToken (token from userStore could be an option).
   */

  async get({url, headers, options, retryConfig, timeout}: IRequestMethod): Promise<any> {
    return this.fetchDataWithRetry({
      url,
      method: 'GET',
      headers,
      options,
      retryConfig,
      timeout
    });
  }

  async post({url, data, headers, options, retryConfig, timeout}: IRequestMethod): Promise<any> {
    return this.fetchDataWithRetry({
      url,
      method: 'POST',
      data,
      headers,
      options,
      retryConfig,
      timeout
    });
  }

  async put({url, data, headers, options, retryConfig, timeout}: IRequestMethod): Promise<any> {
    return this.fetchDataWithRetry({
      url,
      method: 'PUT',
      data,
      headers,
      options,
      retryConfig,
      timeout
    });
  }

  async delete({url, headers, data, options, retryConfig, timeout}: IRequestMethod): Promise<any> {
    return this.fetchDataWithRetry({
      url,
      method: 'DELETE',
      headers,
      data,
      options,
      retryConfig,
      timeout
    });
  }
}

/* eslint-enable @typescript-eslint/no-explicit-any */

export default new FetchService();